import React, { useEffect, useState, useCallback } from "react";
import * as Styled from "./Playlists.styled";
import { ChevronLeft } from "react-bootstrap-icons";
import { TemplateCard } from "../template-card";
import { useNavigate } from "react-router-dom";
import { playlistsService } from "../../../services";
import { Loader } from "../../ui";
import { getLayoutImage, LayoutTemplates, Playlist, Visibility } from "../../../libs";
import SearchIcon from "../../../assets/images/icons/search.svg";
import { MaterialReactTable, useMaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { ThemeProvider, createTheme } from "@mui/material/styles";

export const Playlists: React.FC = () => {
    const navigate = useNavigate();
    const [playlists, setPlaylists] = useState<Playlist[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    const fetchPlaylists = useCallback(async () => {
        setLoading(true);
        try {

            const data = await playlistsService.getAllPlaylists();
            const visiblePlaylists = data.filter(
                (playlist) =>
                    playlist.visibility === Visibility.Public ||
                    playlist.visibility === Visibility.Unlisted
            );
            const formatted = visiblePlaylists.map((playlist) => ({
                ...playlist,
                svgSrc: getLayoutImage(playlist?.templateId ?? LayoutTemplates.Layout1),
            }));
            setPlaylists(formatted);
        } catch (error) {
            console.error("Error fetching playlists:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPlaylists();
    }, [fetchPlaylists]);

    const filteredData = React.useMemo(() => {
        return playlists.filter(item =>
            item.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery, playlists]);

    const handlePlaylistClick = (playlistId: string) => {
        navigate(`/manage-libraries/manage-templates/playlists/${playlistId}`);
    };

    const columns = React.useMemo<MRT_ColumnDef<Playlist>[]>(() => [
        {
            accessorKey: "title",
            header: "Playlist",
            Cell: ({ row }) => {
                const { playlistId, title, templateId } = row.original;
                return (
                    <TemplateCard
                        key={playlistId}
                        title={title}
                        subtitle="All the videos you have stored in the VOD folder will be displayed here."
                        svgSrc={getLayoutImage(templateId ?? LayoutTemplates.Layout1)}
                        onClick={() => handlePlaylistClick(playlistId)}
                    />
                );
            },
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data: filteredData,
        enablePagination: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: false,
        enableBottomToolbar: true,
        initialState: {
            pagination: { pageIndex: 0, pageSize: 10 },
        },
        paginationDisplayMode: 'pages',
        positionPagination: 'bottom',
        muiPaginationProps: {
            showRowsPerPage: true,
            showFirstButton: true,
            showLastButton: true,
        },
        muiTableHeadProps: { sx: { display: 'none' } },
        muiTableBodyRowProps: {
            hover: false,
            sx: {
                '&:hover': {
                    backgroundColor: 'transparent !important',
                },
            },
        },
        muiTableBodyCellProps: {
            sx: {
                backgroundColor: 'transparent !important',
                borderBottom: 'none !important',
                padding: 0,
                margin: 0,
            },
        },
        mrtTheme: {
            baseBackgroundColor: '#0F101300',
            draggingBorderColor: '#b6f84d',
        },
    });


    const theme = createTheme({
        palette: {
            mode: "dark",
        },
    });
    return loading ? (
        <Loader />
    ) : (
        <Styled.StyledContainer>
            <Styled.Header>
                <Styled.HeaderLeftSection>
                    List of Playlists
                </Styled.HeaderLeftSection>
                <Styled.HeaderRightSection>
                    <Styled.DashboardSearchWrapper>
                        <Styled.SearchBarWrapper>
                            <Styled.SearchIcon src={SearchIcon} alt="Search" />
                            <Styled.SearchInput
                                name="search"
                                type="text"
                                placeholder="Search by title"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </Styled.SearchBarWrapper>
                    </Styled.DashboardSearchWrapper>
                    <Styled.CustomButton variant="outline-secondary" onClick={() => navigate(-1)}>Go Back</Styled.CustomButton>
                </Styled.HeaderRightSection>

            </Styled.Header>

            <Styled.Description>
                Here you can organize all the content to see how it will appear on your page.
            </Styled.Description>
            <ThemeProvider theme={theme}>
                <MaterialReactTable table={table} />
            </ThemeProvider>
        </Styled.StyledContainer>
    );
};

